// src/pages/Predictions.js (verified-only access; admins always allowed)
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import RegularPredictions from "./RegularPredictions";
import TablePredictions from "./TablePredictions";
import CompetitionFilterBar from "../components/CompetitionFilterBar";
import SegmentedTabs from "../components/SegmentedTabs";
import PageHeader from "../components/ui/PageHeader";
import PageShell from "../components/ui/PageShell";
import ScoringGuide from "../components/ScoringGuide";
import { usePageTitle } from "../hooks/usePageTitle";
import {
  COMPETITION_OPTIONS,
  filterCompetitionsByEnabled,
  getEnabledCompetitionIds,
  getStoredPreferredCompetitionId,
  pickInitialCompetitionId,
  setStoredPreferredCompetitionId,
} from "../constants/competitionPrefs";
import {
  WC_ACTIVE_SEASON,
  WC_STAGE_OPTIONS,
  getStoredWorldCupStageFilter,
  setStoredWorldCupStageFilter,
} from "../constants/worldCup";

export default function Predictions({ user }) {
  usePageTitle("Predictions");
  const [searchParams] = useSearchParams();

  // ─── Access Helpers (no pricing; verified-only) ───────────
  const isAdmin = !!user?.is_admin;
  const isVerified = !!user?.email_verified;
  const hasActivePlan = isAdmin || isVerified;
  const isAllAccess = isAdmin || isVerified;

  const enabledIds = useMemo(
    () => getEnabledCompetitionIds(user),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.enabled_competitions, user?.id]
  );
  const visibleCompetitions = useMemo(
    () => filterCompetitionsByEnabled(COMPETITION_OPTIONS, enabledIds),
    [enabledIds]
  );

  // ─── State ───────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("regular");
  const [competition, setCompetition] = useState(() =>
    pickInitialCompetitionId(enabledIds, getStoredPreferredCompetitionId())
  );
  const [wcStageFilter, setWcStageFilter] = useState(() => getStoredWorldCupStageFilter());
  const [refreshKey, setRefreshKey] = useState(0);
  const [regularPredictionsState, setRegularPredictionsState] = useState({
    windowState: "open",
    deadline: null,
    openCount: 0,
    remainingOpenCount: 0,
    unsavedCount: 0,
  });

  useEffect(() => {
    const fromUrl = searchParams.get("competition");
    if (fromUrl && enabledIds.includes(fromUrl)) {
      setCompetition(fromUrl);
      return;
    }
    if (!enabledIds.includes(competition)) {
      setCompetition(pickInitialCompetitionId(enabledIds, getStoredPreferredCompetitionId()));
    }
  }, [enabledIds, competition, searchParams]);

  useEffect(() => {
    if ((competition === "wildcard" || competition === "ucl" || competition === "world_cup") && !isAllAccess) {
      const fallback = enabledIds.includes("premier_league") ? "premier_league" : enabledIds[0];
      if (fallback) setCompetition(fallback);
    }
  }, [competition, isAllAccess, enabledIds]);

  useEffect(() => {
    if (competition === "world_cup") setStoredWorldCupStageFilter(wcStageFilter);
  }, [competition, wcStageFilter]);

  // Remember chosen league for next visit (client-only).
  useEffect(() => {
    setStoredPreferredCompetitionId(competition);
  }, [competition]);

  const showTableToggle = !!user?.has_table_prediction_access && competition === "premier_league";

  useEffect(() => {
    if (!showTableToggle && activeTab === "table") setActiveTab("regular");
  }, [showTableToggle, activeTab]);

  if (!hasActivePlan) {
    return (
      <div className="min-h-[70vh] grid place-items-center p-6 text-center bg-paper">
        <div className="max-w-md bg-white rounded-xl border border-line p-6 shadow-sm">
          <h2 className="text-xl font-bold text-ink mb-2">Verify your email to predict</h2>
          <p className="text-muted mb-5">
            Check your inbox for the link, or send a new one.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/verify-email"
              className="inline-flex items-center justify-center h-11 px-5 bg-brand hover:bg-brand-hover text-white rounded-lg font-semibold"
            >
              Resend verification
            </Link>
            <Link
              to="/settings"
              className="inline-flex items-center justify-center h-11 px-5 bg-white border border-line text-gray-700 rounded-lg font-semibold hover:border-gray-400"
            >
              Settings
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { windowState, deadline, openCount = 0, remainingOpenCount = 0 } = regularPredictionsState;
  const deadlineSoon = deadline && deadline.getTime() - Date.now() < 2 * 60 * 60 * 1000;

  let statusLine = null;
  if (activeTab === "regular" && windowState === "open" && openCount > 0) {
    statusLine = (
      <p>
        <span className="font-semibold text-ink">
          {remainingOpenCount > 0 ? `${remainingOpenCount} still to pick` : "All open matches picked"}
        </span>
        {deadline && (
          <>
            {" · next lock "}
            <span className={deadlineSoon ? "font-semibold text-amber-800" : ""}>
              {deadline.toDateString() === new Date().toDateString()
                ? `today ${deadline.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                : deadline.toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}
            </span>
          </>
        )}
      </p>
    );
  }

  const { unsavedCount = 0 } = regularPredictionsState;
  const pickedCount = Math.max(0, openCount - remainingOpenCount);
  const competitionName = visibleCompetitions.find((c) => c.id === competition)?.label || "";

  const rail =
    activeTab === "regular" ? (
      <div className="hidden lg:block space-y-4">
        <section className="rounded-xl border border-line bg-white shadow-sm overflow-hidden">
          <h2 className="px-4 py-3 border-b border-gray-100 text-[15px] font-semibold text-ink">
            Progress{competitionName ? ` · ${competitionName}` : ""}
          </h2>
          <div className="p-4 space-y-3">
            {openCount === 0 ? (
              <p className="text-sm text-muted">No matches open for picks right now.</p>
            ) : (
              <>
                <div>
                  <p className="text-[22px] leading-7 font-bold tabular-nums text-ink">
                    {pickedCount}{" "}
                    <span className="text-[15px] font-medium text-muted">of {openCount} picked</span>
                  </p>
                  <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full bg-brand rounded-full transition-[width]"
                      style={{ width: `${Math.round((pickedCount / openCount) * 100)}%` }}
                    />
                  </div>
                </div>
                <dl className="text-sm space-y-1.5">
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted">Unsaved changes</dt>
                    <dd className={`tabular-nums font-semibold ${unsavedCount ? "text-brand" : "text-ink"}`}>
                      {unsavedCount}
                    </dd>
                  </div>
                  {deadline && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-muted">Next lock</dt>
                      <dd className={`tabular-nums font-semibold ${deadlineSoon ? "text-amber-800" : "text-ink"}`}>
                        {deadline.toDateString() === new Date().toDateString()
                          ? `Today ${deadline.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                          : deadline.toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })}
                      </dd>
                    </div>
                  )}
                </dl>
                <p className="text-[12px] text-muted">
                  Each match locks before kickoff; its countdown shows when. Unsaved picks stay on this device until you save.
                </p>
              </>
            )}
          </div>
        </section>
        <ScoringGuide />
      </div>
    ) : null;

  return (
    <div className="min-h-screen bg-paper text-ink">
      <PageShell rail={rail} stickyRail>
        <div className="space-y-4">
        <PageHeader
          title="Predict"
          meta={statusLine}
          actions={
            <Link
              to="/fixtures"
              className="inline-flex items-center h-9 px-3 rounded-lg border border-line bg-white text-sm font-semibold text-gray-700 hover:border-gray-400"
            >
              Fixtures
            </Link>
          }
        />

        <CompetitionFilterBar
          items={visibleCompetitions.map((c) => ({
            key: c.id,
            label: c.label,
            disabled: (c.id === "ucl" || c.id === "wildcard" || c.id === "world_cup") && !isAllAccess,
          }))}
          activeKey={competition}
          onChange={setCompetition}
        >
          {competition === "world_cup" && (
            <div>
              <p className="text-[12px] font-medium text-muted mb-1.5">World Cup {WC_ACTIVE_SEASON} stage</p>
              <SegmentedTabs
                items={WC_STAGE_OPTIONS.map((s) => ({ key: s.id, label: s.label }))}
                activeKey={wcStageFilter}
                onChange={setWcStageFilter}
              />
            </div>
          )}
          {showTableToggle && (
            <SegmentedTabs
              items={[
                { key: "regular", label: "Matches" },
                { key: "table", label: "Final table" },
              ]}
              activeKey={activeTab}
              onChange={setActiveTab}
            />
          )}
        </CompetitionFilterBar>

        <div className="pt-1">
          {activeTab === "regular" ? (
            <RegularPredictions
              key={`${competition}-${wcStageFilter || "all"}`}
              competition={competition}
              wcStageFilter={wcStageFilter}
              refreshKey={refreshKey}
              onRefresh={() => setRefreshKey((k) => k + 1)}
              onEditingStateChange={setRegularPredictionsState}
            />
          ) : (
            <TablePredictions user={user} />
          )}
        </div>
        </div>
      </PageShell>
    </div>
  );
}
